/* src/components/Admin/Organizations/CompanyInfo.tsx */
import React from 'react';
import {
  Globe,
  Phone,
  Linkedin,
  Twitter,
  Facebook,
  Building2,
  Users,
  Calendar,
  ShieldAlert,
} from 'lucide-react';
import { OrganizationItem } from '../../../api/adminOrganizationsService';
import { useTranslation } from 'react-i18next';

interface Props {
  item: OrganizationItem | null;
}

const riskColor = (risk: number) =>
  risk >= 80
    ? 'bg-red-600'
    : risk >= 50
    ? 'bg-yellow-500'
    : 'bg-green-600';

const CompanyInfo: React.FC<Props> = ({ item }) => {
  const { t } = useTranslation();

  if (!item) {
    return (
      <div className="h-44 flex items-center justify-center border rounded-xl bg-white shadow">
        <p className="text-gray-400">{t('admin.select_company_hint')}</p>
      </div>
    );
  }

  const { org, report } = item;

  /* ───── helpers de fila ───── */
  const Line = ({
    icon,
    children,
  }: {
    icon: React.ReactNode;
    children: React.ReactNode;
  }) => (
    <div className="flex items-start gap-2">
      {icon}
      <span>{children}</span>
    </div>
  );

  return (
    <div className="border rounded-xl bg-white shadow p-5 text-sm space-y-4">
      {/* ───────────────────────── Header ───────────────────────── */}
      <div className="flex items-center gap-4">
        {org.logo_url ? (
          <img
            src={org.logo_url}
            alt={`${org.name} logo`}
            className="h-12 w-12 rounded-md object-contain bg-gray-100 p-1"
          />
        ) : (
          <div className="h-12 w-12 flex items-center justify-center rounded-md bg-gray-100">
            <Building2 className="h-6 w-6 text-gray-400" />
          </div>
        )}

        <h3 className="text-lg font-semibold">{org.name}</h3>

        {/* Risk pill */}
        <span
          className={`ml-auto px-3 py-1 rounded-full text-white text-xs font-semibold ${riskColor(
            report.risk,
          )}`}
        >
          {t('admin.risk')}: {report.risk.toFixed(0)}
        </span>
      </div>

      {/* ───────────────────────── Links / basics ───────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {org.website_url && (
          <Line icon={<Globe className="h-4 w-4 text-gray-500" />}>
            <a
              href={org.website_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {org.website_url}
            </a>
          </Line>
        )}
        {org.linkedin_url && (
          <Line icon={<Linkedin className="h-4 w-4 text-blue-600" />}>
            <a
              href={org.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              LinkedIn
            </a>
          </Line>
        )}
        {org.twitter_url && (
          <Line icon={<Twitter className="h-4 w-4 text-sky-500" />}>
            <a
              href={org.twitter_url}
              target="_blank"
              rel="noreferrer"
              className="text-sky-500 hover:underline break-all"
            >
              Twitter
            </a>
          </Line>
        )}
        {org.facebook_url && (
          <Line icon={<Facebook className="h-4 w-4 text-blue-700" />}>
            <a
              href={org.facebook_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:underline break-all"
            >
              Facebook
            </a>
          </Line>
        )}
        {org.phone && (
          <Line icon={<Phone className="h-4 w-4 text-gray-500" />}>
            {org.phone}
          </Line>
        )}
        {org.founded_year && (
          <Line icon={<Calendar className="h-4 w-4 text-gray-500" />}>
            {t('admin.founded')}: {org.founded_year}
          </Line>
        )}
        {org.estimated_num_employees && (
          <Line icon={<Users className="h-4 w-4 text-gray-500" />}>
            {t('admin.estimated_employees')}: {org.estimated_num_employees}
          </Line>
        )}
        {org.primary_domain && (
          <Line icon={<Globe className="h-4 w-4 text-gray-500" />}>
            {t('admin.primary_domain')}: {org.primary_domain}
          </Line>
        )}
        {(org.city || org.country) && (
          <Line icon={<Building2 className="h-4 w-4 text-gray-500" />}>
            {org.city ? `${org.city}, ` : ''}
            {org.country ?? ''}
          </Line>
        )}
      </div>

      {org.clean_description && (
        <p className="text-xs italic text-gray-600">{org.clean_description}</p>
      )}

      {/* ─────────────── Mini–dashboard de métricas ─────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
        {/* ejemplo de métrica */}
        <Metric
          label={t('admin.breaches')}
          value={report.len_data_breaches}
          icon={<ShieldAlert className="h-4 w-4" />}
        />
        <Metric label={t('admin.vulns')} value={report.len_vulns} />
        <Metric label={t('admin.emails')} value={report.len_emails} />
        <Metric label={t('admin.ips')} value={report.len_ips} />
        <Metric label={t('admin.employees')} value={report.len_employees} />
        <Metric
          label={t('admin.price')}
          value={`€${report.price.toFixed(2)}`}
        />
      </div>
    </div>
  );
};

/* Componente interno para cada KPI */
const Metric = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
}) => (
  <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
    {icon}
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  </div>
);

export default CompanyInfo;
